<script setup lang="ts">
    import { useFilterStoreByName } from '@/js/stores/filter';
    import dayjs from 'dayjs';
    import RemovableActiveFilter from './RemovableActiveFilter.vue';
    import { __, __untypedExpression } from '@/js/stores/expressions';
    import { computed, nextTick, onMounted, ref, watch } from 'vue';
    import {
        FilterStoreName,
        isCheckboxFilterType,
        RelativeDateRangeDto,
        RelativeRangePreset,
    } from '@/js/services/api/filter/types';
    import EchoButton from '../EchoButton.vue';
    import RemovableActiveCheckboxFilter from './RemovableActiveCheckboxFilter.vue';
    import { isNotNullOrUndefined } from '@/js/utils/isNullOrUndefined';
    import { defaultDateRange, startOfWeekMonday } from '@/js/utils/dateUtils';
    import LoadingDots from '../LoadingDots.vue';
    import { ChevronDownIcon, XIcon } from '@lucide/vue';
    import { showTrackerGroupTypeToggle } from './types';

    const props = defineProps<{
        expanded: boolean;
        showClear?: boolean;
        filterStoreName: FilterStoreName;
        readonly?: boolean;
    }>();

    const emit = defineEmits<{
        (e: 'reset-filters'): void;
        (e: 'update:expanded', expanded: boolean): void;
    }>();

    /**
     * Fixed cutoff: show the first 3 active filters, hide the rest behind a
     * "+N more" toggle, regardless of viewport width.
     */
    const VISIBLE_FILTER_COUNT = 3;

    const activeFiltersList = ref<HTMLElement | null>(null);
    const renderedFilterCount = ref(0);

    /**
     * Counts rendered chips directly: filter-state fields can default to
     * `false` instead of null, which would overcount chips that never render.
     */
    const updateRenderedFilterCount = () => {
        renderedFilterCount.value =
            activeFiltersList.value?.querySelectorAll(':scope > li:not(.filter-overflow-toggle)')
                .length ?? 0;
    };

    onMounted(updateRenderedFilterCount);

    const hiddenFilterCount = computed(() =>
        Math.max(0, renderedFilterCount.value - VISIBLE_FILTER_COUNT)
    );

    const filter = useFilterStoreByName(props.filterStoreName);

    watch(
        () => filter.selected,
        () => nextTick(updateRenderedFilterCount),
        { deep: true }
    );

    const hideDateFilter = props.filterStoreName === FilterStoreName.ScheduleCoachingFilter;

    const dateRange = ref('');

    watch(
        () => [filter.all?.startTime, filter.all?.endTime, filter.all?.relativeDateRange],
        () => {
            const rel = filter.all?.relativeDateRange as RelativeDateRangeDto | null | undefined;

            if (rel) {
                dateRange.value = resolveRelativeLabel(rel);
                return;
            }

            dateRange.value = resolveFormattedDateRange(filter.all?.startTime, filter.all?.endTime);
        },
        { immediate: true, deep: true }
    );

    function resolveRelativeLabel(r: RelativeDateRangeDto): string {
        switch (r.preset) {
            case RelativeRangePreset.Today:
                return __('today');
            case RelativeRangePreset.Yesterday:
                return __('yesterday');
            case RelativeRangePreset.ThisWeek:
                return __('this-week');
            case RelativeRangePreset.LastWeek:
                return __('last-week');
            case RelativeRangePreset.MonthToDate:
                return __('this-month-to-date');
            case RelativeRangePreset.LastMonth:
                return __('last-month');
            case RelativeRangePreset.YearToDate:
                return __('this-year-to-date');
            case RelativeRangePreset.LastNDays: {
                const days = r.value ?? 7;
                return __('last-n-days', { days: String(days) });
            }
            case RelativeRangePreset.LastNMonths: {
                const months = r.value ?? 6;
                return __('last-n-months', { months: String(months) });
            }
            default:
                return '';
        }
    }

    function resolveFormattedDateRange(
        startTime: string | Date | null | undefined,
        endTime: string | Date | null | undefined
    ): string {
        if (!startTime || !endTime) return '';
        const presets = {
            'last-30-days': {
                start: dayjs().subtract(30, 'days').format('YYYYMMDD'),
                end: dayjs().format('YYYYMMDD'),
            },
            'last-3-months': {
                start: dayjs().subtract(3, 'month').add(1, 'day').format('YYYYMMDD'),
                end: dayjs().format('YYYYMMDD'),
            },
            'last-6-months': {
                start: dayjs().subtract(6, 'month').add(1, 'day').format('YYYYMMDD'),
                end: dayjs().format('YYYYMMDD'),
            },
            'last-12-months': {
                start: dayjs().subtract(1, 'year').add(1, 'day').format('YYYYMMDD'),
                end: dayjs().format('YYYYMMDD'),
            },
            'yesterday': {
                start: dayjs().subtract(1, 'day').format('YYYYMMDD'),
                end: dayjs().subtract(1, 'day').format('YYYYMMDD'),
            },
            'last-7-days': {
                start: dayjs().subtract(6, 'day').format('YYYYMMDD'),
                end: dayjs().format('YYYYMMDD'),
            },
            'this-week': {
                start: startOfWeekMonday().format('YYYYMMDD'),
                end: dayjs().format('YYYYMMDD'),
            },
            'month-to-date': {
                start: dayjs().startOf('month').format('YYYYMMDD'),
                end: dayjs().format('YYYYMMDD'),
            },
            'year-to-date': {
                start: dayjs().startOf('year').format('YYYYMMDD'),
                end: dayjs().format('YYYYMMDD'),
            },
        };

        const formattedStart = dayjs(startTime).format('YYYYMMDD');
        const formattedEnd = dayjs(endTime).format('YYYYMMDD');

        const match = Object.entries(presets).find(
            ([, { start, end }]) => start === formattedStart && end === formattedEnd
        );

        return match ? __untypedExpression(match[0]) : `${formattedStart}-${formattedEnd}`;
    }

    const resetDateRange = () => {
        if (filter.all) {
            filter.all.relativeDateRange = null;
            const range = defaultDateRange();
            filter.all.startTime = range.start;
            filter.all.endTime = range.end;
        }
    };

    const removeSelectedCustomItem = (key: string, item: string | number | boolean) => {
        if (filter.all?.customDataFilter) {
            filter.all?.customDataFilter.forEach((custom) => {
                if (custom.key === key && isCheckboxFilterType(custom.filterType)) {
                    custom.filter?.forEach((checkbox) => {
                        if (checkbox.id === item) {
                            checkbox.isSelected = false;
                        }
                    });
                } else if (custom.key === key && custom.filterType === 'Text') {
                    custom.words = custom.words?.filter((word) => word !== item);
                }
            });
        }
    };

    const resetTrackerSelectedGroupType = () => {
        if (filter.all?.trackerFilter) {
            filter.all.trackerFilter.selectedGroupType = 'All';
        }
    };

    const removeWord = (words: string[] | undefined, word: string) => {
        if (words) {
            const index = words.indexOf(word);
            if (index !== -1) {
                words.splice(index, 1);
            }
        }
    };

    const resetFilters = () => {
        filter.resetFilter();
        emit('reset-filters');
    };

    const resetOnlyResolved = () => {
        if (filter.all) {
            filter.all.onlyResolved = null;
        }
    };

    function resetHasTranscriptions(): void {
        if (filter.all) {
            filter.all.hasTranscriptions = true;
        }
    }

    const isDefaultDateRange = () => {
        if (!filter.all?.startTime || !filter.all?.endTime) return false;
        if (filter.all.relativeDateRange) return false;
        const range = defaultDateRange();
        return (
            dayjs(filter.all.startTime).format('YYYYMMDD') ===
                dayjs(range.start).format('YYYYMMDD') &&
            dayjs(filter.all.endTime).format('YYYYMMDD') === dayjs(range.end).format('YYYYMMDD')
        );
    };

    function clearAiFilters() {
        if (!filter.all) return;
        filter.all.createdByAi = null;
        filter.all.editedByHuman = null;
    }
</script>

<template>
    <ul
        v-if="filter.all"
        ref="activeFiltersList"
        class="contents"
        :class="{ 'active-filters-collapsed': !props.expanded }"
    >
        <li v-if="dateRange && !hideDateFilter">
            <RemovableActiveFilter
                :filter-name="__('date')"
                :removable="!props.readonly && !isDefaultDateRange()"
                @remove-filter="resetDateRange"
            >
                {{ dateRange }}
            </RemovableActiveFilter>
        </li>

        <li v-if="filter.selected.onlyResolved === true || filter.selected.onlyResolved === false">
            <RemovableActiveFilter
                :removable="!props.readonly"
                @remove-filter="resetOnlyResolved()"
            >
                {{ filter.selected.onlyResolved ? __('resolved-issues') : __('unresolved-issues') }}
            </RemovableActiveFilter>
        </li>

        <RemovableActiveCheckboxFilter
            :removable="!props.readonly"
            :filter="filter.all.protocolFormDefinitionFilter"
            :checkboxes="filter.selected.protocolFormDefinitionFilter"
        />

        <RemovableActiveCheckboxFilter
            :removable="!props.readonly"
            :filter="filter.all.submittedByUserFilter"
            :checkboxes="filter.selected.submittedByUserFilter"
        />

        <RemovableActiveCheckboxFilter
            :removable="!props.readonly"
            :filter="filter.all.protocolAgentFilter"
            :checkboxes="filter.selected.protocolAgentFilter"
        />

        <RemovableActiveCheckboxFilter
            :removable="!props.readonly"
            :filter="filter.all.protocolGradeFilter"
            :checkboxes="filter.selected.protocolGradeFilter"
        />

        <li v-if="isNotNullOrUndefined(filter.selected.submittedByMe)">
            <RemovableActiveFilter
                :removable="!props.readonly"
                @remove-filter="filter.all.submittedByMe = null"
            >
                {{
                    filter.selected.submittedByMe
                        ? __('table-protocol-column-submitted-by-me')
                        : __('table-protocol-column-not-submitted-by-me')
                }}
            </RemovableActiveFilter>
        </li>

        <li
            v-if="
                isNotNullOrUndefined(filter.selected.createdByAi) || filter.selected.editedByHuman
            "
        >
            <RemovableActiveFilter :removable="!props.readonly" @remove-filter="clearAiFilters">
                {{
                    filter.selected.editedByHuman
                        ? __('ai-human-edited')
                        : filter.selected.createdByAi
                          ? __('ai-buddy')
                          : __('manual')
                }}
            </RemovableActiveFilter>
        </li>

        <li v-if="isNotNullOrUndefined(filter.selected.pendingApproval)">
            <RemovableActiveFilter
                :removable="!props.readonly"
                @remove-filter="filter.all.pendingApproval = null"
            >
                {{
                    filter.selected.pendingApproval
                        ? __('protocol-pending-approval')
                        : __('protocol-approved')
                }}
            </RemovableActiveFilter>
        </li>

        <li v-if="isNotNullOrUndefined(filter.selected.actionRequired)">
            <RemovableActiveFilter
                :removable="!props.readonly"
                @remove-filter="filter.all.actionRequired = null"
            >
                {{
                    filter.selected.actionRequired
                        ? __('table-protocol-column-action-required')
                        : __('table-protocol-column-no-action-required')
                }}
            </RemovableActiveFilter>
        </li>

        <RemovableActiveCheckboxFilter
            :removable="!props.readonly"
            :filter="filter.all.skillFilter"
            :checkboxes="filter.selected.skillFilter"
        />

        <RemovableActiveCheckboxFilter
            :removable="!props.readonly"
            :filter="filter.all.queueFilter"
            :checkboxes="filter.selected.queueFilter"
        />

        <RemovableActiveCheckboxFilter
            :removable="!props.readonly"
            :filter="filter.all.levelFilter"
            :checkboxes="filter.selected.levelFilter"
        />

        <li
            v-if="
                showTrackerGroupTypeToggle &&
                filter.selected.trackerFilter?.selectedGroupType &&
                filter.selected.trackerFilter?.selectedGroupType !== 'All'
            "
        >
            <RemovableActiveFilter @remove-filter="resetTrackerSelectedGroupType()">
                {{
                    __('tracker-filter', {
                        type: filter.selected.trackerFilter?.selectedGroupType as string,
                    })
                }}
            </RemovableActiveFilter>
        </li>

        <RemovableActiveCheckboxFilter
            :removable="!props.readonly"
            :filter="filter.all.trackerFilter?.filter"
            :checkboxes="filter.selected.trackerFilter?.filter"
        />

        <RemovableActiveCheckboxFilter
            :removable="!props.readonly"
            :filter="filter.all.channelFilter"
            :checkboxes="filter.selected.channelFilter"
            expression-base="channel-name-"
        />

        <RemovableActiveCheckboxFilter
            :removable="!props.readonly"
            :filter="filter.all.categoryFilter"
            :checkboxes="filter.selected.categoryFilter"
        />

        <RemovableActiveCheckboxFilter
            :removable="!props.readonly"
            :filter="filter.all.sentimentAgentFilter"
            :checkboxes="filter.selected.sentimentAgentFilter"
            icon-base="sentiment"
            expression-base="sentiment-"
        />

        <RemovableActiveCheckboxFilter
            :removable="!props.readonly"
            :filter="filter.all.sentimentAgentDirectionFilter"
            :checkboxes="filter.selected.sentimentAgentDirectionFilter"
            icon-base="sentimentdirection"
            expression-base="sentiment-"
        />

        <RemovableActiveCheckboxFilter
            :removable="!props.readonly"
            :filter="filter.all.sentimentCustomerFilter"
            :checkboxes="filter.selected.sentimentCustomerFilter"
            icon-base="sentiment"
            expression-base="sentiment-"
        />

        <RemovableActiveCheckboxFilter
            :removable="!props.readonly"
            :filter="filter.all.sentimentCustomerDirectionFilter"
            :checkboxes="filter.selected.sentimentCustomerDirectionFilter"
            icon-base="sentimentdirection"
            expression-base="sentiment-"
        />

        <li v-if="filter.all && filter.selected.recordingStart && filter.all.recordingStart">
            <RemovableActiveFilter
                :removable="!props.readonly"
                @remove-filter="filter.all.recordingStart = 0"
            >
                {{ filter.recordingStartLabel }}
            </RemovableActiveFilter>
        </li>

        <li v-if="filter.all && filter.selected.recordingEnd && filter.all.recordingEnd">
            <RemovableActiveFilter
                :removable="!props.readonly"
                @remove-filter="filter.all.recordingEnd = 0"
            >
                {{ filter.recordingEndLabel }}
            </RemovableActiveFilter>
        </li>

        <li v-for="item in filter.selected.keywordFilter?.words" :key="'keyword-' + item">
            <RemovableActiveFilter
                :removable="!props.readonly"
                @remove-filter="removeWord(filter.all.keywordFilter?.words, item)"
            >
                {{ item }}
            </RemovableActiveFilter>
        </li>

        <li
            v-for="item in filter.selected.keywordFilter?.excludedWords"
            :key="'excluded-keyword-' + item"
        >
            <RemovableActiveFilter
                :removable="!props.readonly"
                @remove-filter="removeWord(filter.all.keywordFilter?.excludedWords, item)"
            >
                {{ item }}
            </RemovableActiveFilter>
        </li>

        <template v-for="custom in filter.selected.customDataFilter" :key="custom.key">
            <template v-if="isCheckboxFilterType(custom.filterType)">
                <li v-for="item in custom.filter" :key="item.name">
                    <RemovableActiveFilter
                        :removable="!props.readonly"
                        @remove-filter="removeSelectedCustomItem(custom.key, item.id)"
                    >
                        {{ item.name }}
                    </RemovableActiveFilter>
                </li>
            </template>

            <template v-if="custom.filterType === 'Text'">
                <li v-for="item in custom.words" :key="item">
                    <RemovableActiveFilter
                        :removable="!props.readonly"
                        @remove-filter="removeSelectedCustomItem(custom.key, item)"
                    >
                        {{ item }}
                    </RemovableActiveFilter>
                </li>
            </template>
        </template>

        <li v-if="filter.selected.hasTranscriptions === false">
            <RemovableActiveFilter
                :removable="!props.readonly"
                @remove-filter="resetHasTranscriptions()"
            >
                {{ __('include-unanalyzed') }}
            </RemovableActiveFilter>
        </li>

        <li v-if="hiddenFilterCount > 0" class="filter-overflow-toggle">
            <EchoButton variant="chip-dashed" @click="emit('update:expanded', !props.expanded)">
                {{
                    props.expanded
                        ? __('filter-show-fewer')
                        : __('filter-show-more', { count: String(hiddenFilterCount) })
                }}
                <template #icon-right>
                    <ChevronDownIcon :class="{ 'rotate-180': props.expanded }" />
                </template>
            </EchoButton>
        </li>
    </ul>
    <template v-if="filter.all && !props.readonly && (showClear || $slots['favorite-filters'])">
        <span class="w-px h-5.5 bg-gray-light shrink-0" aria-hidden="true" />
        <template v-if="showClear">
            <EchoButton size="small" variant="secondary" @click="resetFilters()">
                {{ __('reset-filters') }}
                <template #icon-right><XIcon /></template>
            </EchoButton>
            <slot name="save-filter" />
        </template>
        <slot name="favorite-filters" />
    </template>

    <div v-if="!filter.all" class="mt-px mb-px pt-1.5 pb-2">
        <LoadingDots :color="false" />
    </div>
</template>
<style scoped>
    /* :deep() needed: RemovableActiveCheckboxFilter's <li>s are separate
       fragment roots, so Vue's scoped-CSS attribute never reaches them. */
    /* Keep n+4 in sync with VISIBLE_FILTER_COUNT+1 above: CSS can't read a JS constant. */
    .active-filters-collapsed > :deep(li:nth-child(n + 4):not(.filter-overflow-toggle)) {
        display: none;
    }
</style>
